import {
  AddUserArgs,
  AddUserResponse,
  AddUserClaimArgs,
  RemoveUserClaimArgs,
  AddServiceUserArgs,
  AddServiceUserResponse,
  GetUsersResponse,
  StandardRequest,
  StandardResponse,
  GetMyContactsRequest,
  GetMyContactsResponse,
  GetContactRequest,
  GetContactResponse,
  AddContactRequest,
  GetAllContactsRequest,
  GetAllContactsResponse,
  DeleteContactRequest,
  EditContactRequest,
  InviteContactRequest,
  InviteContactResponse,
  UnlinkContactRequest,
  UnlinkContactResponse,
  SendForgotPasswordEmailRequest,
  SendForgotPasswordEmailResponse,
  MagicLink,
  GetMagicLinksResponse,
  AddContactResponse,
  ILoginWithCredentialsRequest,
  ILoginWithCredentialsResponse,
  ITerminateAccountRequest,
  ITerminateAccountResponse,
  IUpdateProfilePictureRequest,
  IUpdateProfilePictureResponse,
  IGetUserRequest,
  IGetUserResponse,
  IUpdateUserArgs,
} from "common247";

import { callFunction } from "./helpers";
import { AppUserModel } from "common247";
import { FirebaseServices } from "./config";
import { trackContactAdded } from "../analytics";
import { entityCache } from "../sync/entity-cache";

export async function getAllContacts(fb: FirebaseServices, request: GetAllContactsRequest): Promise<GetAllContactsResponse> {
  const result = await callFunction<GetAllContactsRequest, GetAllContactsResponse>(fb, "getAllContactsFunc", request);
  return result;
}

export async function addContact(fb: FirebaseServices, request: AddContactRequest): Promise<AddContactResponse> {
  const result = await callFunction<AddContactRequest, AddContactResponse>(fb, "addContactFunc", request);
  if (result && (result as any).status !== false) {
    trackContactAdded((result as any).id || "contact", Boolean(request.email && request.email.trim().length > 0));
  }
  return result;
}

/**
 * Retrieves detailed user information and storage quota by User UID.
 *
 * @param {FirebaseServices} fb - The Firebase services instance.
 * @param {IGetUserRequest} request - The request containing user UID.
 * @returns {Promise<IGetUserResponse>} The user details and quota response.
 */
export async function getUser(fb: FirebaseServices, request: IGetUserRequest): Promise<IGetUserResponse> {
  return await callFunction<IGetUserRequest, IGetUserResponse>(fb, "getUserFunc", request);
}

export async function getUsersFunc(fb: FirebaseServices): Promise<AppUserModel[]> {
  const result = await callFunction<StandardRequest, GetUsersResponse>(fb, "getUsersFunc", {});
  return result.users;
}

export async function getMyContacts(fb: FirebaseServices, request: GetMyContactsRequest): Promise<GetMyContactsResponse> {
  const result = await callFunction<GetMyContactsRequest, GetMyContactsResponse>(fb, "getMyContactsFunc", request);
  if (result && Array.isArray(result.contacts)) {
    entityCache.setCachedList("contact", result.contacts, "myContacts");
  }
  return result;
}

/**
 * Retrieves a single contact by ID.
 *
 * @param {FirebaseServices} fb - The Firebase services instance.
 * @param {GetContactRequest} request - The request containing contactId.
 * @returns {Promise<GetContactResponse>} The retrieved contact response.
 */
export async function getContact(fb: FirebaseServices, request: GetContactRequest): Promise<GetContactResponse> {
  const result = await callFunction<GetContactRequest, GetContactResponse>(fb, "getContactFunc", request);
  return result;
}

export async function ensureAllContactsGroup(fb: FirebaseServices, userUid: string): Promise<StandardResponse> {
  const result = await callFunction<{ ownerId: string }, StandardResponse>(fb, "ensureAllContactsGroupFunc", { ownerId: userUid });
  return result;
}

export async function addUserFunc(fb: FirebaseServices, args: AddUserArgs): Promise<AppUserModel> {
  const result = await callFunction<AddUserArgs, AddUserResponse>(fb, "addUserFunc", args);
  return result.user;
}

/**
 * Adds a custom claim to a user account.
 *
 * @param {FirebaseServices} fb - The Firebase services instance.
 * @param {AddUserClaimArgs} args - Arguments containing user email and claim JSON string.
 * @returns {Promise<StandardResponse>}
 */
export async function addUserClaimFunc(fb: FirebaseServices, args: AddUserClaimArgs): Promise<StandardResponse> {
  return await callFunction<AddUserClaimArgs, StandardResponse>(fb, "addUserClaimFunc", args);
}

/**
 * Removes a custom claim from a user account.
 *
 * @param {FirebaseServices} fb - The Firebase services instance.
 * @param {RemoveUserClaimArgs} args - Arguments containing user email and claimName.
 * @returns {Promise<StandardResponse>}
 */
export async function removeUserClaimFunc(fb: FirebaseServices, args: RemoveUserClaimArgs): Promise<StandardResponse> {
  return await callFunction<RemoveUserClaimArgs, StandardResponse>(fb, "removeUserClaimFunc", args);
}

/**
 * Creates a new service account user.
 *
 * @param {FirebaseServices} fb - The Firebase services instance.
 * @param {AddServiceUserArgs} args - Arguments containing service user details and claims.
 * @returns {Promise<AppUserModel>}
 */
export async function addServiceUserFunc(fb: FirebaseServices, args: AddServiceUserArgs): Promise<AppUserModel> {
  const result = await callFunction<AddServiceUserArgs, AddServiceUserResponse>(fb, "addServiceUserFunc", args);
  return result.user;
}

/**
 * Resolves a username or email identifier to an email address via resolveEmailForLoginFunc.
 * If the input already contains an '@', returns it immediately without network request.
 *
 * @param fb - FirebaseServices instance.
 * @param username - Username or email string to resolve.
 * @returns Object with status boolean, resolved email, or error.
 */
export async function resolveEmailForLogin(
  fb: FirebaseServices,
  username: string,
): Promise<{ status: boolean; email?: string; error?: string }> {
  if (!username) return { status: false, error: "Username is required." };
  const trimmed = username.trim().toLowerCase();
  if (trimmed.includes("@")) {
    return { status: true, email: trimmed };
  }
  return await callFunction<{ username: string }, { status: boolean; email?: string; error?: string }>(
    fb,
    "resolveEmailForLoginFunc",
    { username: trimmed },
  );
}

/**
 * Authenticates with either a username or email address and password,
 * returning a custom Firebase Auth token upon successful verification.
 *
 * @param {FirebaseServices} fb - The Firebase services instance.
 * @param {ILoginWithCredentialsRequest} args - The login credentials.
 * @returns {Promise<ILoginWithCredentialsResponse>} The authentication result with custom token.
 */
export async function loginWithCredentials(
  fb: FirebaseServices,
  args: ILoginWithCredentialsRequest,
): Promise<ILoginWithCredentialsResponse> {
  return await callFunction<ILoginWithCredentialsRequest, ILoginWithCredentialsResponse>(
    fb,
    "loginWithCredentialsFunc",
    args,
  );
}

export async function deleteContact(fb: FirebaseServices, request: DeleteContactRequest): Promise<StandardResponse> {
  const result = await callFunction<DeleteContactRequest, StandardResponse>(fb, "deleteContactFunc", request);
  return result;
}

export async function editContact(fb: FirebaseServices, request: EditContactRequest): Promise<StandardResponse> {
  const result = await callFunction<EditContactRequest, StandardResponse>(fb, "editContactFunc", request);
  return result;
}

export async function unlinkContact(fb: FirebaseServices, request: UnlinkContactRequest): Promise<UnlinkContactResponse> {
  const result = await callFunction<UnlinkContactRequest, UnlinkContactResponse>(fb, "unlinkContactFunc", request);
  return result;
}

export async function inviteContact(fb: FirebaseServices, request: InviteContactRequest): Promise<InviteContactResponse> {
  const result = await callFunction<InviteContactRequest, InviteContactResponse>(fb, "sendContactInvitationFunc", request);
  return result;
}

export async function sendForgotPasswordEmail(fb: FirebaseServices, request: SendForgotPasswordEmailRequest): Promise<SendForgotPasswordEmailResponse> {
  return await callFunction<SendForgotPasswordEmailRequest, SendForgotPasswordEmailResponse>(fb, "sendForgotPasswordEmailFunc", request);
}

/**
 * Fetches all magic links from the Firestore db via cloud function.
 *
 * @param fb - The Firebase services instance.
 * @returns A promise resolving to the list of MagicLinks.
 */
export async function getMagicLinksFunc(fb: FirebaseServices): Promise<MagicLink[]> {
  const result = await callFunction<any, GetMagicLinksResponse>(fb, "getMagicLinksFunc", {});
  return result.magicLinks || [];
}

/**
 * Calls the registerInvitedUserFunc cloud function to register a new user using their magic link invitation.
 * @param {FirebaseServices} fb - The Firebase services instance.
 * @param {object} args - The registration details containing magic link ID and password.
 * @param {string} args.id - The magic link invitation GUID.
 * @param {string} args.password - The user-defined password.
 * @returns {Promise<{ status: boolean; error?: string; token?: string }>} The status and the custom sign-in token if successful.
 */
export async function registerInvitedUser(fb: FirebaseServices, args: { id: string; password: string }): Promise<{ status: boolean; error?: string; token?: string }> {
  return await callFunction<any, any>(fb, "registerInvitedUserFunc", args);
}

/**
 * Updates user attributes including display name, storage quota in bytes, and/or custom claims.
 *
 * @param {FirebaseServices} fb - The Firebase services instance.
 * @param {IUpdateUserArgs} args - Update arguments.
 * @returns {Promise<StandardResponse>} Result of update.
 */
export async function updateUserFunc(fb: FirebaseServices, args: IUpdateUserArgs): Promise<StandardResponse> {
  return await callFunction<IUpdateUserArgs, StandardResponse>(fb, "updateUserFunc", args);
}

/**
 * Permanently terminates the user's account and purges owned data under the Right of Deletion.
 *
 * @param {FirebaseServices} fb - The Firebase services instance.
 * @param {ITerminateAccountRequest} request - The termination request with confirmation text.
 * @returns {Promise<ITerminateAccountResponse>} The server response.
 */
export async function terminateAccount(
  fb: FirebaseServices,
  request: ITerminateAccountRequest
): Promise<ITerminateAccountResponse> {
  return await callFunction<ITerminateAccountRequest, ITerminateAccountResponse>(
    fb,
    "terminateAccountFunc",
    request
  );
}

/**
 * Uploads user profile photo (200x200) and small icon (50x50), scanning for content safety.
 *
 * @param {FirebaseServices} fb - The Firebase services instance.
 * @param {IUpdateProfilePictureRequest} request - The profile picture upload payload.
 * @returns {Promise<IUpdateProfilePictureResponse>} The server response with photo and icon URLs.
 */
export async function updateProfilePicture(
  fb: FirebaseServices,
  request: IUpdateProfilePictureRequest
): Promise<IUpdateProfilePictureResponse> {
  return await callFunction<IUpdateProfilePictureRequest, IUpdateProfilePictureResponse>(
    fb,
    "updateProfilePictureFunc",
    request
  );
}
